import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {Observable, of, OperatorFunction, Subject} from 'rxjs';
import {debounceTime, filter, mergeAll, switchMap, tap} from 'rxjs/operators';
import {AsaxHeaderService, HeaderFilter} from 'asax-common';
import {NgbTooltip, NgbTypeahead} from '@ng-bootstrap/ng-bootstrap';
import {ElasticValidator, ElasticValidatorFactory} from 'asax-elastic';
import {TypeaheadService} from "../typeahead.service";
import {searchInputDebounceTime} from "../../search-input-text/search-input-text.component";
import {TypeaheadSearchInput} from "../../search-input.model";

@Component({
  selector: 'asax-header-search-input-typeahead',
  templateUrl: './search-input-typeahead.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchInputTypeaheadComponent implements OnInit, OnChanges {
  @ViewChild('typeahead', {static: true}) typeaheadComponent: NgbTypeahead;
  @ViewChild('tooltip', {static: false}) tooltip: NgbTooltip;

  @Input() searchInput: TypeaheadSearchInput;

  @Output() outFilter = new EventEmitter<HeaderFilter>();

  isInvalid: boolean;
  errorMessage: string;
  isSuggestionsListLoading: boolean;

  valueSubject = new Subject<string>();
  valueSubjectWithDebounceTime = new Subject<string>();

  elasticValidator: ElasticValidator;

  valueIsValidatedSubject = new Subject();

  search: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) => text$.pipe(
    debounceTime(500),
    tap(() => this.setIsSuggestionsListLoading(true)),
    tap(() => this.tooltip?.open()),
    switchMap(term => {
      if (this.isInvalid) {
        return of([])
      } else {
        return this.searchTypeaheadResults(term)
      }
    }),
    tap(() => this.setIsSuggestionsListLoading(false)),
    tap(() => this.tooltip?.close())
  );

  constructor(private readonly asaxHeaderService: AsaxHeaderService,
              private readonly typeaheadService: TypeaheadService,
              private readonly elasticValidatorFactory: ElasticValidatorFactory,
              private readonly changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.elasticValidator = this.elasticValidatorFactory.getElasticValidator(this.searchInput.elasticValidatorType);

    this.valueIsValidatedSubject.asObservable().pipe(
      filter(() => this.searchInput.id === 'projectAbstract'),
      tap(() => this.asaxHeaderService.setProjectAbstractSearchInputText(this.searchInput?.value))
    ).subscribe();

    this.valueIsValidatedSubject.asObservable().pipe(
      filter(() => this.searchInput.id === 'bibAbstract'),
      tap(() => this.asaxHeaderService.setPublicationAbstractSearchInputText(this.searchInput?.value))
    ).subscribe();

    of(
      this.valueSubjectWithDebounceTime.asObservable().pipe(debounceTime(searchInputDebounceTime)),
      this.valueSubject.asObservable()
    ).pipe(
      mergeAll(),
      tap(() => this.isInvalid = undefined),
      tap(() => this.validateValue()),
      tap(() => this.changeDetectorRef.markForCheck()),
      tap(() => this.valueIsValidatedSubject.next(undefined)),
      tap(() => this.emitFilter())
    ).subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.valueChanged();
  }

  valueChanged(): void {
    this.valueSubject.next(undefined);
  }

  valueChangedWithDebounceTime(): void {
    this.valueSubjectWithDebounceTime.next(undefined);
  }

  setIsSuggestionsListLoading(loading: boolean): void {
    this.isSuggestionsListLoading = loading;
    this.changeDetectorRef.markForCheck();
  }

  validateValue(): void {
    const {isInvalid, errorMessage} = this.elasticValidator?.validate(this.searchInput.value);

    this.isInvalid = isInvalid;
    this.errorMessage = errorMessage;
  }

  clearSearchInputValue(): void {
    this.searchInput.value = '';

    this.validateValue();
    this.emitFilter();
  }

  onExampleClick(example: string): void {
    this.searchInput.value = example;
    this.valueChanged();
  }

  searchTypeaheadResults(term: string): Observable<string[]> {
    return this.typeaheadService.search(term, this.searchInput.typeaheadIndex, this.searchInput.typeaheadKeyword);
  }

  emitFilter(): void {
    const filter = {
      id: this.searchInput.id,
      value: this.searchInput.value,
      valueParsed: this.searchInput.value,
      valueToDisplay: this.searchInput.value,
      label: this.searchInput.label,
      isDisabled: this.searchInput.isDisabled,
      isInvalid: this.isInvalid,
      elasticParserType: this.searchInput.elasticParserType
    };

    this.outFilter.emit(filter);
  }
}
